import useAuthStore from '~/authentication/store';

import { WlWindow } from '../../typings/globals/cordova';
import { fullUserAgent, isCordova, isIosApp } from './platform';

declare const window: WlWindow;

export type ErrorResponse = {
  error: Error;
  parsedResponse: Record<string, string>;
  responseText: string;
  responseJson: string;
  status: number;
  statusText: string;
  url: string;
};

const getCredentialsRequestOption = (
  tokenFromStorage: string | undefined,
): RequestCredentials | undefined => {
  if (tokenFromStorage && isCordova()) {
    return undefined; // No credentials needed if token is present and we're in the app
  }
  return 'include'; // Include credentials for cross-origin requests without a token
};

const checkStatus = (response: Response): Promise<Response | ErrorResponse> => {
  const event = new CustomEvent('Good Connection');
  window.dispatchEvent(event);
  if (response.status >= 200 && response.status < 300) {
    return Promise.resolve(response);
  }
  const responseClone = response.clone();
  const error = new Error(responseClone.statusText);
  return responseClone
    .clone()
    .text()
    .then((responseText) => {
      let parsedResponse;
      let canParseResponse;
      try {
        parsedResponse = JSON.parse(responseText);
        canParseResponse = true;
      } catch (e) {
        parsedResponse = responseText;
        canParseResponse = false;
      }

      return Promise.reject({
        error,
        parsedResponse,
        responseText,
        responseJson: canParseResponse ? parsedResponse : undefined,
        status: responseClone.status,
        statusText: responseClone.statusText,
        url: responseClone.url,
      });
    });
};

const errorHandler = (error) => {
  if (error instanceof TypeError) {
    const event = new CustomEvent('Bad Connection');
    window.dispatchEvent(event);
  }

  return Promise.reject(error);
};

const parseJSON = (response: Response): any => {
  if (!!response) {
    return response.text().then((text) => {
      try {
        return text ? JSON.parse(text) : {};
      } catch (e) {
        return {
          responseText: text,
        };
      }
    });
  }
  return response;
};

const withRetries = (fn, totalRetries = 3, attempts = 0) =>
  fn().catch((e) => {
    if (attempts === totalRetries) {
      return Promise.reject(e);
    } else if (e instanceof TypeError && e.message === 'Failed to fetch') {
      return new Promise((resolve, reject) => {
        setTimeout(
          () =>
            withRetries(fn, totalRetries, attempts + 1)
              .then(resolve)
              .catch(reject),
          (attempts + 1) * 1000,
        );
      });
    } else {
      return Promise.reject(e);
    }
  });

const httpRequest = (url: string) => {
  const tokenFromStorage = useAuthStore.getState().token;

  const extraHeaders: { [name: string]: string } = isIosApp()
    ? { 'User-Agent': fullUserAgent() }
    : {};
  if (tokenFromStorage) {
    extraHeaders.Authorization = `Bearer ${tokenFromStorage}`;
  }

  return {
    get: (
      data?: any,
      headers?: { [name: string]: string },
      responseInterceptor: (response: Response) => Response = (r) => r,
      corsMode?: RequestMode,
    ) =>
      withRetries(() => {
        const finalHeaders: { [name: string]: string } | undefined = corsMode
          ? undefined
          : { ...(headers || {}), ...extraHeaders };
        if (!corsMode && !!data) {
          (finalHeaders as any)['Content-Type'] = 'application/json';
        }
        return fetch(url, {
          mode: corsMode,
          body: undefined,
          method: 'GET',
          credentials: corsMode
            ? undefined
            : getCredentialsRequestOption(tokenFromStorage),
          headers: finalHeaders,
        })
          .then(responseInterceptor)
          .then(checkStatus)
          .then(parseJSON)
          .catch(errorHandler);
      }),
    post: (
      data?: any,
      headers?: { [name: string]: string },
      responseInterceptor: (response: Response) => Response = (r) => r,
    ) =>
      withRetries(() => {
        const finalHeaders: { [name: string]: string } = {
          ...(headers || {}),
          ...extraHeaders,
        };
        if (data) finalHeaders['Content-Type'] = 'application/json';
        return fetch(url, {
          body: data ? JSON.stringify(data) : undefined,
          method: 'POST',
          mode: 'cors',
          credentials: getCredentialsRequestOption(tokenFromStorage),
          headers: finalHeaders,
        })
          .then(responseInterceptor)
          .then(checkStatus)
          .then(parseJSON)
          .catch(errorHandler);
      }),
    put: (
      data?: any,
      headers?: { [name: string]: string },
      responseInterceptor: (response: Response) => Response = (r) => r,
    ) =>
      withRetries(() => {
        const finalHeaders: { [name: string]: string } = {
          ...(headers || {}),
          ...extraHeaders,
        };
        if (data) finalHeaders['Content-Type'] = 'application/json';
        return fetch(url, {
          body: data ? JSON.stringify(data) : undefined,
          method: 'PUT',
          mode: 'cors',
          credentials: getCredentialsRequestOption(tokenFromStorage),
          headers: finalHeaders,
        })
          .then(responseInterceptor)
          .then(checkStatus)
          .then(parseJSON)
          .catch(errorHandler);
      }),
    delete: (
      data?: any,
      headers?: { [name: string]: string },
      responseInterceptor: (response: Response) => Response = (r) => r,
    ) =>
      withRetries(() => {
        const finalHeaders: { [name: string]: string } = {
          ...(headers || {}),
          ...extraHeaders,
        };
        if (data) finalHeaders['Content-Type'] = 'application/json';
        return fetch(url, {
          body: data ? JSON.stringify(data) : undefined,
          method: 'DELETE',
          mode: 'cors',
          credentials: getCredentialsRequestOption(tokenFromStorage),
          headers: finalHeaders,
        })
          .then(responseInterceptor)
          .then(checkStatus)
          .then(parseJSON)
          .catch(errorHandler);
      }),
    patch: (
      data?: any,
      headers?: { [name: string]: string },
      responseInterceptor: (response: Response) => Response = (r) => r,
    ) =>
      withRetries(() => {
        const finalHeaders: { [name: string]: string } = {
          ...(headers || {}),
          ...extraHeaders,
        };
        if (data) finalHeaders['Content-Type'] = 'application/json';
        return fetch(url, {
          body: data ? JSON.stringify(data) : undefined,
          method: 'PATCH',
          mode: 'cors',
          credentials: getCredentialsRequestOption(tokenFromStorage),
          headers: finalHeaders,
        })
          .then(responseInterceptor)
          .then(checkStatus)
          .then(parseJSON)
          .catch(errorHandler);
      }),
  };
};

export default httpRequest;
